import { format as formatDate, formatDistanceToNow } from 'date-fns'

// The house absolute-datetime format ("July 16th 2026, 9:21 PM") — replaces
// the browser's default `toLocaleString()` shape ("7/16/2026, 9:21:08 PM")
// that every un-formatted `type: 'dateTime'` table column was falling back to.
export const ABSOLUTE_DATETIME_FORMAT = 'MMMM do yyyy, h:mm a'

// Relative "x ago" for activity feeds (e.g. "2 hours ago", "5 minutes ago").
// Pair with the absolute `datetime()` in a tooltip so precision stays one hover away.
// date-fns prefixes approximate distances with "about" — render it as the "~"
// approximation symbol instead ("about 2 hours ago" → "~2 hours ago").
export const timeAgo = (date: string): string | null => {
  if (!date) return null
  return formatDistanceToNow(new Date(date), { addSuffix: true }).replace('about ', '~')
}

export const date = (date: string, format = 'MMM dd yyyy'): string | null => {
  if (!date) return null
  return formatDate(new Date(date), format)
}

/**
 * Format a value that is a CALENDAR DATE, not an instant — a publication date, a
 * period, anything the source expressed as "22 August" with no time of day.
 *
 * ## Why `date()` is wrong for these
 *
 * A date-only value arrives as UTC midnight (`2026-08-22T00:00:00.000Z`), and `date()`
 * renders in the viewer's local zone. West of UTC that is the PREVIOUS evening, so the
 * formatted day is off by one: an article Luxe Digital published on 22 August rendered
 * "Aug 21 2026" in America/New_York. Measured, not theorised — that is what sent me
 * looking at whether the ingested dates were wrong, when the data was correct all along.
 *
 * Reading the same UTC fields the value was built from sidesteps the zone entirely. A
 * calendar date has no zone, so converting it into one and back out can only lose.
 *
 * ⚠️ Do NOT use this for a timestamp that IS an instant — `created_at`, `last_polled_at`,
 * anything that happened at a moment. Those are correctly shown in the viewer's own time,
 * and forcing them to UTC would make every one of them wrong instead.
 */
export const dateOnly = (value: string, format = 'MMM dd yyyy'): string | null => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  // Rebuild as a LOCAL date carrying the UTC calendar fields, so `formatDate` prints the
  // day the source meant regardless of where it is read.
  return formatDate(
    new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()),
    format,
  )
}

export const time = (date: string, format = 'HH:mm'): string | null => {
  if (!date) return null
  return formatDate(new Date(date), format)
}

export const datetime = (date: string, format = ABSOLUTE_DATETIME_FORMAT) => {
  if (!date) return null
  return formatDate(new Date(date), format)
}

// Relative for anything from the last 24h ("X minutes/hours ago"), absolute date once
// older — a table column reads "3 hours ago" as recent activity, but "47 days ago" is
// less scannable than "Jun 20 2026". 24h is the cutoff (not a separate "Yesterday"
// bucket) to keep this a two-state, no-surprises rule. Always pair with a tooltip
// showing `datetime()` for full precision, same convention as `timeAgo` above.
export const relativeOrDate = (value: string, dateFormat = 'MMM dd yyyy'): string | null => {
  if (!value) return null
  const ageMs = Date.now() - new Date(value).getTime()
  const DAY_MS = 24 * 60 * 60 * 1000
  // Math.abs, not a bare ageMs < DAY_MS — a FUTURE value (a due date, an
  // upcoming renewal) has a negative ageMs, which is "less than" any positive
  // DAY_MS regardless of how far out it is. Unguarded, every future date would
  // wrongly read as relative ("in 47 days" instead of the absolute date).
  return Math.abs(ageMs) < DAY_MS ? timeAgo(value) : date(value, dateFormat)
}

// Same relative/absolute split as `relativeOrDate`, but the absolute branch
// keeps the TIME component (ABSOLUTE_DATETIME_FORMAT) instead of date-only —
// for columns that show a moment (created/claimed/scanned), not just a day
// (manufactured date, birthday). This is the one that belongs on every
// "dateTime"-type table column and every `datetime()` display call site.
export const relativeOrDateTime = (value: string, absoluteFormat = ABSOLUTE_DATETIME_FORMAT): string | null => {
  if (!value) return null
  const ageMs = Date.now() - new Date(value).getTime()
  const DAY_MS = 24 * 60 * 60 * 1000
  // Math.abs — see relativeOrDate's comment above; same future-date bug applies
  // here (a due date, a scheduled renewal, an expiry).
  return Math.abs(ageMs) < DAY_MS ? timeAgo(value) : datetime(value, absoluteFormat)
}

// ALWAYS absolute, never relative — for anywhere the display is meant to be
// read later/out of context (a CSV/print export, an email, a PDF), where
// "43 minutes ago" would be meaningless by the time it's opened.
export const absoluteDateTime = (value: string, format = ABSOLUTE_DATETIME_FORMAT): string | null => {
  if (!value) return null
  return datetime(value, format)
}

/**
 * VRG-705 — the house display for a LAST-ACTIVITY moment (last click, last seen, last visit):
 *   primary  "Oct 14, 3:42 PM"  in the viewer's own zone (the year only when it is not this year)
 *   relative "~2 hours ago"     (`timeAgo`)
 *   title    "October 14th 2026, 3:42 PM · America/Chicago (UTC-05:00)" for the hover
 * Composed from `datetime` and `timeAgo` above, not a new format. First built for Tracked links'
 * "Last click" (VRG-703); `ActivityTimeCell` renders it in a table, and inline text uses the parts.
 * `now` and `timeZone` are injectable for the guard; callers leave them out.
 */
export type ActivityStamp = { primary: string; relative: string; title: string }

export const activityStamp = (
  value: string | null | undefined,
  now: Date = new Date(),
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): ActivityStamp | null => {
  if (!value) return null
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return null
  const sameYear = at.getFullYear() === now.getFullYear()
  return {
    primary: datetime(value, sameYear ? 'MMM d, h:mm a' : 'MMM d yyyy, h:mm a') ?? '',
    relative: timeAgo(value) ?? '',
    title: `${datetime(value)} · ${timeZone} (UTC${datetime(value, 'xxx')})`,
  }
}
